import { useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight } from "lucide-react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { getPostBySlug, getAllPostsSorted } from "@/content/blog/posts";
import { PageNav, PageFooter } from "@/components/blog/PageChrome";
import { EASE, FADE_UP_20, FADE_UP_30, VISIBLE, TITLE_TRANSITION } from "@/lib/motion";

const dateFmt = (iso) =>
  new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }).toUpperCase();

const EXCERPT_TRANSITION = { duration: 0.9, delay: 0.15 };
const HAIRLINE_TRANSITION = { duration: 1.1, delay: 0.35, ease: EASE };
const REMARK_PLUGINS = [remarkGfm];

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPostBySlug(slug);

  useEffect(() => {
    document.title = post ? `${post.title} — Himanshu Oberoi` : "Not found — Himanshu Oberoi";
    window.scrollTo(0, 0);
  }, [post]);

  if (!post) {
    return (
      <main className="relative min-h-screen bg-obsidian text-white overflow-x-hidden" data-testid="blogpost-notfound">
        <div className="grain" aria-hidden />
        <PageNav />
        <section className="container-x pt-40 pb-32">
          <p className="eyebrow">/// 404</p>
          <h1 className="mt-6 font-serif font-light text-5xl md:text-7xl text-ink leading-[0.96]">
            This piece isn't published yet.
          </h1>
          <Link to="/blog" data-testid="blogpost-back-list" className="btn-sweep inline-flex items-center gap-3 border border-stroke text-ink px-6 py-4 font-mono text-xs tracking-[0.24em] uppercase mt-10">
            <ArrowLeft size={16} strokeWidth={1.5} />
            Back to Journal
          </Link>
        </section>
        <PageFooter />
      </main>
    );
  }

  const all = getAllPostsSorted();
  const currentIdx = all.findIndex((p) => p.slug === post.slug);
  const next = all[currentIdx + 1] || null;

  return (
    <main className="relative min-h-screen bg-obsidian text-white overflow-x-hidden" data-testid="blogpost-root">
      <div className="grain" aria-hidden />
      <PageNav />

      <article className="container-x pt-32 md:pt-40 pb-16">
        <Link to="/blog" data-testid="blogpost-back" className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.28em] uppercase text-steel hover:text-laser transition-colors">
          <ArrowLeft size={14} strokeWidth={1.5} />
          Journal
        </Link>

        <div className="mt-10 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-3">
            <div className="font-mono text-[10px] tracking-[0.28em] uppercase text-laser">
              {dateFmt(post.date)}
            </div>
            <div className="mt-2 font-mono text-[10px] tracking-[0.24em] uppercase text-steel">
              {post.readTime}
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {post.tags.map((t) => (
                <span key={t} className="font-mono text-[10px] tracking-[0.24em] uppercase text-steel border border-stroke px-3 py-1.5">
                  {t}
                </span>
              ))}
            </div>
          </div>

          <div className="col-span-12 md:col-span-9">
            <motion.h1
              initial={FADE_UP_30}
              animate={VISIBLE}
              transition={TITLE_TRANSITION}
              className="font-serif font-light text-4xl md:text-6xl lg:text-7xl leading-[0.98] tracking-tight text-ink"
              data-testid="blogpost-title"
            >
              {post.title}
            </motion.h1>
            <motion.p
              initial={FADE_UP_20}
              animate={VISIBLE}
              transition={EXCERPT_TRANSITION}
              className="mt-6 font-serif italic text-xl md:text-2xl text-steel leading-relaxed max-w-3xl"
            >
              {post.excerpt}
            </motion.p>
            <div className="mt-6 font-mono text-[10px] tracking-[0.28em] uppercase text-steel">
              — {post.author}
            </div>
          </div>
        </div>

        <motion.div
          initial={{ scaleX: 0 }}
          animate={{ scaleX: 1 }}
          transition={HAIRLINE_TRANSITION}
          className="mt-16 origin-left hairline"
        />

        <div className="mt-16 grid grid-cols-12 gap-6 md:gap-10">
          <div className="col-span-12 md:col-span-9 md:col-start-4">
            <div className="prose-post" data-testid="blogpost-body">
              <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{post.content}</ReactMarkdown>
            </div>
          </div>
        </div>

        {next && (
          <div className="mt-24 md:mt-32 border-t border-stroke pt-10" data-testid="blogpost-next">
            <p className="eyebrow mb-4">/// Read next</p>
            <Link to={`/blog/${next.slug}`} data-testid="blogpost-next-link" className="group grid grid-cols-12 gap-4 items-center">
              <div className="col-span-12 md:col-span-10">
                <h3 className="font-serif font-light text-3xl md:text-5xl text-ink leading-tight tracking-tight group-hover:text-laser transition-colors">
                  {next.title}
                </h3>
              </div>
              <div className="col-span-12 md:col-span-2 md:justify-self-end">
                <ArrowUpRight size={32} strokeWidth={1} className="text-steel group-hover:text-laser transition-colors" />
              </div>
            </Link>
          </div>
        )}
      </article>

      <PageFooter />
    </main>
  );
}
